import * as api from './api'
import * as reducers from './reducers-slice'
import { AppDispatch } from 'src/app/store'
import { extractFormData, getFileFromBlobUrl } from 'src/core/helpers'
import { getInactiveProjectTransformed } from './transformers/inactive-project.transformer'
import {
  checkProjectAcquisitionErrors,
  checkTransferProjectErrors,
  checkBrokerageErrors,
} from './transformers/error-helpers'
import {
  CreateBrokerageProjectPayload,
  ProjectAcquisitionPayload,
  PurchaseBrokeragePayload,
  ProjectDiscountPayload,
  ProjectApiPayload,
  Project,
} from 'src/core/models'

export const createProject = (
  payload: CreateBrokerageProjectPayload, isVintage: boolean
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setCreateProjectLoading(true))
    const transformedData = getInactiveProjectTransformed(payload, isVintage)
    const data = await api.createprojectAPI(transformedData)
    const filesData = { main_image: payload.main_image ? await getFileFromBlobUrl(payload.main_image) : '' }
    const formData = extractFormData(filesData)
    await api.updateProjectFilesAPI(data.id, formData)
    dispatch(reducers.setCreateProjectResponse(data))
    dispatch(reducers.setCreateProjectLoading(false))
    dispatch(reducers.setCreateProjectSuccess(true))
  } catch (error) {
    dispatch(reducers.setCreateProjectSuccess(false))
    dispatch(reducers.setCreateProjectLoading(false))
  }
}

export const validateProjectId = (id: number, ignore_validation=false) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setValidateProjectIdLoading(true))
    const project = await api.getProjectDetailsAPI(id, ignore_validation)
    dispatch(reducers.setValidateProjectIdLoading(false))
    dispatch(reducers.setValidateProjectIdResponse(project))
  } catch (error) {
    dispatch(reducers.setValidateProjectIdLoading(false))
    dispatch(reducers.setValidateProjectIdResponse('ERROR'))
  }
}

export const validateProjectIdForStockSale = (id: number, ignore_validation=false) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setValidateProjectIdForStockSaleLoading(true))
    const project: Project = await api.getProjectDetailsAPI(id, ignore_validation)
    dispatch(reducers.setValidateProjectIdForStockSaleLoading(false))
    dispatch(reducers.setValidateProjectIdForStockSaleLoading(false))
    dispatch(reducers.setValidateProjectIdForStockSaleResponse(project))
  } catch (error) {
    dispatch(reducers.setValidateProjectIdForStockSaleLoading(false))
    dispatch(reducers.setValidateProjectIdForStockSaleResponse('ERROR'))
  }
}


export const createPurchaseOperation = (payload: ProjectAcquisitionPayload) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setPurchaseOperationLoading(true))
    await api.createPurchaseOperationAPI(payload)
    dispatch(reducers.setPurchaseOperationLoading(false))
    dispatch(reducers.setPurchaseOperationSuccess(true))
  } catch (error) {
    dispatch(reducers.setPurchaseOperationLoading(false))
    dispatch(reducers.setPurchaseOperationSuccess(false))
    checkProjectAcquisitionErrors(dispatch, error)
  }
}

export const createSaleBrokerageOperation = (payload: PurchaseBrokeragePayload) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setBrokerageOperationLoading(true))
    const newPurchaseUrl: string = await api.createSaleBrokerageOperationAPI(payload)
    dispatch(reducers.setBrokerageOperationLoading(false))
    dispatch(reducers.setBrokerageOperationNewLink(newPurchaseUrl))
    dispatch(reducers.setBrokerageOperationSuccess(true))
  } catch (error) {
    dispatch(reducers.setBrokerageOperationLoading(false))
    dispatch(reducers.setBrokerageOperationSuccess(false))
    checkBrokerageErrors(dispatch, error)
  }
}

export const getUserProjects = (userId: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setUserProjectsLoading(true))
    const projects: Project[] = await api.getUserProjectsAPI(userId)
    dispatch(reducers.setUserProjectsData(projects))
    dispatch(reducers.setUserProjectsLoading(false))
  } catch (error) {
    dispatch(reducers.setUserProjectsLoading(false))
  }
}


export const getProjectDetails = (projectId: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setProjectDetailsLoading(true))
    const project: Project = await api.getProjectDetailsAPI(projectId)
    dispatch(reducers.setProjectDetailsData(project))
    dispatch(reducers.setProjectDetailsData(project))
    dispatch(reducers.setProjectDetailsLoading(false))
  } catch (error) {
    dispatch(reducers.setProjectDetailsLoading(false))
  }
}

export const transferProject = (userId: number, projectId: number, email: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setTransferProjectLoading(true))
    await api.transferProjectAPI(projectId, email)
    dispatch(reducers.setTransferProjectSuccess(true))
    dispatch(getUserProjects(userId))
    dispatch(reducers.setTransferProjectLoading(false))
  } catch (error) {
    dispatch(reducers.setTransferProjectSuccess(false))
    dispatch(reducers.setTransferProjectLoading(false))
    checkTransferProjectErrors(dispatch, error)
  }
}

export const deleteProjectImage = (imageId: number) => async () => {
  try {
    await api.deleteProjectImageAPI(imageId)
  } catch (error) {
    console.warn(error)
  }
}

export const createBigProject = (
  projectPayload: ProjectApiPayload, projectFiles: FormData,
  projectImages: File[], discounts: ProjectDiscountPayload[]
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setCreateBigProjectLoading(true))
    const newProject: Project = await api.createBigProjectAPI(projectPayload)
    await api.createProjectDiscountsAPI(newProject, discounts)
    await api.updateProjectFilesAPI(newProject.id, projectFiles)
    if (projectImages.length > 0) {
      await api.uploadProjectImagesapi(newProject.id, projectImages)
    }
    dispatch(reducers.setCreateBigProjectNewId(newProject.id))
    dispatch(reducers.setCreateBigProjectLoading(false))
  } catch (error) {
    dispatch(reducers.setCreateBigProjectNewId(null))
    dispatch(reducers.setCreateBigProjectLoading(false))
    dispatch(reducers.setCreateBigProjectError(error))
  }
}

export const editBigProject = (
  project: Project, projectPayload: ProjectApiPayload,
  projectFiles: FormData, newImages: File[], discounts: ProjectDiscountPayload[]
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(reducers.setEditBigProjectLoading(true))
    const newProjectId = await api.editBigProjectAPI(project.id, projectPayload)
    await api.manageDiscountEditionAPI(project, discounts)
    await api.updateProjectFilesAPI(newProjectId, projectFiles)
    if (newImages.length > 0) {
      await api.uploadProjectImagesapi(newProjectId, newImages)
    }
    dispatch(reducers.setEditBigProjectSuccess(true))
    dispatch(reducers.setEditBigProjectLoading(false))
  } catch (error) {
    dispatch(reducers.setEditBigProjectSuccess(false))
    dispatch(reducers.setEditBigProjectLoading(false))
    dispatch(reducers.setCreateBigProjectError(error?.response?.data))
  }
}

export const deleteProjectDiscount = (discountId: number) => async () => {
  try {
    await api.deleteProjectDiscountAPI(discountId)
  } catch (error) {
    console.warn(error)
  }
}
